import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCommunity } from "@/context/CommunityContext";
import TopBar from "@/components/TopBar";
import UserPicker from "@/components/UserPicker";

export default function LoveTree() {
  const { user } = useAuth();
  const { community, slug } = useCommunity();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ display_name: "", partner_id: null, message: "", date_text: "", visibility: "PUBLIC" });
  const [partnerName, setPartnerName] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!slug) return;
    api.get(`/communities/${slug}/love-tree`).then(({ data: payload }) => setData(payload)).catch(() => setData(null));
  }, [slug]);

  useEffect(load, [load]);

  useEffect(() => {
    if (user?.display_name) setForm((f) => ({ ...f, display_name: f.display_name || user.display_name }));
  }, [user]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.message.trim()) {
      setError("Bitte schreibe eure Botschaft.");
      return;
    }
    if (!form.partner_id) {
      setError("Bitte wähle die zweite Person aus.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/love-tree/messages", { ...form, community_id: community?.id });
      setSent(true);
      setOpen(false);
      setForm({ display_name: user?.display_name || "", partner_id: null, message: "", date_text: "", visibility: "PUBLIC" });
      load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const config = data?.config;

  return (
    <div data-testid="love-tree-page">
      <TopBar back="/" title={config?.title || "Liebesbaum"} />
      <div className="relative h-56 w-full bg-[var(--brand-soft)] grain">
        {config?.hero_image ? <img src={fileUrl(config.hero_image)} alt="" className="h-full w-full object-cover" /> : null}
      </div>
      <div className="mx-auto max-w-2xl px-5 py-7">
        <h1 className="text-4xl">{config?.title || "Liebesbaum"}</h1>
        <p className="mt-3 text-base leading-relaxed">{config?.intro_text || "Hinterlasst gemeinsam eure Liebesbotschaft."}</p>

        <ol className="mt-6 space-y-2 text-sm text-[var(--ink-soft)]">
          <li>1 Nachricht schreiben</li>
          <li>2 Partner einladen</li>
          <li>3 Partner bestätigt</li>
          <li>4 Nachricht wird öffentlich</li>
        </ol>

        {sent ? (
          <p className="mt-6 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid="love-sent">
            Einladung gesendet. Die Botschaft wird erst öffentlich, wenn {partnerName || "die zweite Person"} bestätigt.
          </p>
        ) : null}

        {!user ? (
          <div className="mt-6 rounded-2xl border bg-white p-5">
            <p className="text-sm">Für einen Eintrag brauchst du ein Konto.</p>
            <Link to="/login" className="btn btn-primary mt-3 !py-2.5 !text-xs" data-testid="love-login">ANMELDEN</Link>
          </div>
        ) : (
          <button className="btn btn-accent mt-6 w-full" onClick={() => setOpen(!open)} data-testid="love-create-open">
            <Heart className="h-4 w-4" /> LIEBESBOTSCHAFT ERSTELLEN
          </button>
        )}

        {open ? (
          <form onSubmit={submit} className="mt-5 space-y-4 rounded-3xl border bg-white p-5 rise" data-testid="love-form">
            <div>
              <div className="eyebrow mb-1">Dein Anzeigename</div>
              <input className="field" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="love-display-name" />
            </div>
            <UserPicker
              value={form.partner_id}
              onChange={(id, name) => {
                setForm({ ...form, partner_id: id });
                setPartnerName(name);
              }}
              label="Partner einladen"
              testId="love-partner"
            />
            <div>
              <div className="eyebrow mb-1">Botschaft</div>
              <textarea className="field min-h-[120px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="love-message" />
            </div>
            <div>
              <div className="eyebrow mb-1">Datum (optional)</div>
              <input className="field" placeholder="z.B. Sommer 2026" value={form.date_text} onChange={(e) => setForm({ ...form, date_text: e.target.value })} data-testid="love-date" />
            </div>
            <div>
              <div className="eyebrow mb-1">Sichtbarkeit</div>
              <select className="field" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="love-visibility">
                <option value="PUBLIC">Öffentlich am Liebesbaum</option>
                <option value="PRIVATE">Nur für uns zwei</option>
              </select>
            </div>
            {error ? <p className="text-sm text-red-700" data-testid="love-error">{error}</p> : null}
            <button className="btn btn-primary w-full" disabled={busy} data-testid="love-submit">EINLADUNG SENDEN</button>
          </form>
        ) : null}

        <section className="mt-10" data-testid="love-messages">
          <p className="eyebrow">Botschaften</p>
          <div className="mt-4 space-y-3">
            {(data?.messages || []).length === 0 ? (
              <p className="rounded-2xl border bg-white p-5 text-sm text-[var(--ink-soft)]">Noch keine bestätigten Botschaften.</p>
            ) : null}
            {(data?.messages || []).map((message) => (
              <article key={message.id} className="rounded-3xl border bg-white p-5" data-testid={`love-message-${message.id}`}>
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4" style={{ color: "var(--brand-accent)" }} />
                  <p className="text-lg">
                    {message.author_name} & {message.partner_name}
                  </p>
                </div>
                <p className="mt-3 whitespace-pre-line text-base leading-relaxed">{message.message}</p>
                <p className="mt-3 text-xs text-[var(--ink-soft)]">
                  {message.date_text ? `${message.date_text} · ` : ""}
                  {community?.short_name || community?.name}
                </p>
              </article>
            ))}
          </div>
        </section>

        {config?.outro_text ? <p className="mt-8 text-sm text-[var(--ink-soft)]">{config.outro_text}</p> : null}
      </div>
    </div>
  );
}
